import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from './GlassCard';
import FuturisticButton from './FuturisticButton';

const { FiAlertTriangle, FiCheck, FiX } = FiIcons;

const DisclaimerModal = ({ isOpen, onClose, onAccept }) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm overflow-y-auto">
        <div className="min-h-full flex items-start sm:items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="disclaimer-title"
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            className="w-full max-w-2xl my-auto"
          >
            <GlassCard className="p-4 sm:p-6" hover={false}>
              <div className="flex justify-between items-center mb-5">
                <h2 id="disclaimer-title" className="text-xl sm:text-2xl font-bold text-white terminal-text">
                  Medical and Privacy Notice
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-green-400 hover:bg-white/10"
                  aria-label="Close medical notice"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>

              <GlassCard className="p-4 mb-5 border-l-4 border-red-400" hover={false}>
                <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-red-400 mb-2" />
                <h3 className="font-semibold text-red-400 mb-2">Not medical advice</h3>
                <p className="text-sm text-white">
                  This tool provides an automated educational explanation. It does not
                  diagnose, treat, prescribe, or replace a qualified healthcare professional.
                  Call 000 for an emergency in Australia.
                </p>
              </GlassCard>

              <div className="space-y-3 text-sm sm:text-base text-green-100">
                <p>
                  <strong className="text-white">AI limitations:</strong> Results may be
                  incomplete, incorrect, outdated, or affected by OCR errors. Confirm the
                  source report and all interpretations with your clinician.
                </p>
                <p>
                  <strong className="text-white">Data processing:</strong> The selected
                  document is processed in your browser, and extracted report text is sent
                  directly to OpenAI using your API key. OpenAI processing is governed by
                  your OpenAI account terms and data controls.
                </p>
                <p>
                  <strong className="text-white">Sensitive information:</strong> Remove
                  names, addresses, Medicare numbers, dates of birth, and other identifiers
                  before uploading whenever possible.
                </p>
                <p>
                  <strong className="text-white">Your responsibility:</strong> Do not delay
                  or change medical care, medication, or treatment because of an AI response.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <FuturisticButton onClick={onClose} variant="outline" className="sm:flex-1">
                  Cancel
                </FuturisticButton>
                <FuturisticButton onClick={onAccept} icon={FiCheck} className="sm:flex-1">
                  I Understand and Wish to Continue
                </FuturisticButton>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default DisclaimerModal;